var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { chain, groupBy, max, round, sumBy, times } from 'lodash';
import { useMemo } from 'react';
import * as yup from 'yup';
import { ValidationError } from 'yup';
import { MetricTarget, PlanTypeCategory, ShrinkageCategory } from '../../enum';
import { WizardStep } from './constants';
import { useQueueAPI, useQueueStatus } from './queue/selectors';
import { WorkType } from './queue/types';
import { usePlanTypeCategory, useWizardParams } from './selectors';
import { stripDisplaySuffix } from './shrinkage/quickInputDialog';
import { useWAVMonths } from './workArrivalPattern/selectors';
export var generateValidateField = function () {
    var fields = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        fields[_i] = arguments[_i];
    }
    var shaped = {};
    for (var _a = 0, fields_1 = fields; _a < fields_1.length; _a++) {
        var field = fields_1[_a];
        if (field === 'prodOccupancy') {
            shaped[field] = yup.number().nullable().required().min(0.01, 'Occupancy must be greater than 0');
        }
        else if (field === 'productionHour') {
            shaped[field] = yup.number().nullable().required().min(0, 'Value must be positive');
        }
        else {
            shaped[field] = yup.number().nullable().required();
        }
    }
    return yup.array().of(yup.object().shape(shaped)).required();
};
var getVolumesValidation = function (fields, hasQueueLocations) {
    if (hasQueueLocations === void 0) { hasQueueLocations = false; }
    if (hasQueueLocations) {
        return {
            queueLocations: yup
                .array()
                .of(yup.object().shape({
                volumes: generateValidateField.apply(void 0, fields),
            }))
                .required(),
        };
    }
    return {
        volumes: generateValidateField.apply(void 0, fields),
    };
};
var getAllocationPeriodCount = function (queueLocations, volumes) { var _a, _b, _c; return (_c = (_a = volumes === null || volumes === void 0 ? void 0 : volumes.length) !== null && _a !== void 0 ? _a : max((_b = queueLocations === null || queueLocations === void 0 ? void 0 : queueLocations.map(function (location) { var _a, _b; return (_b = (_a = location.volumes) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0; })) !== null && _b !== void 0 ? _b : [])) !== null && _c !== void 0 ? _c : 0; };
var isMissingAllocationValue = function (value) { return value == null || value === '' || Number.isNaN(Number(value)); };
var validateRequiredLocationAllocationValues = function (context, queueLocations, volumes, fieldName) {
    if (!(queueLocations === null || queueLocations === void 0 ? void 0 : queueLocations.length))
        return true;
    var totalPeriods = getAllocationPeriodCount(queueLocations, volumes);
    var errors = queueLocations.flatMap(function (location, locationIdx) {
        return times(totalPeriods, function (periodIdx) {
            var _a, _b;
            var value = (_b = (_a = location.volumes) === null || _a === void 0 ? void 0 : _a[periodIdx]) === null || _b === void 0 ? void 0 : _b[fieldName];
            return isMissingAllocationValue(value)
                ? context.createError({
                    path: "".concat(context.path, ".").concat(locationIdx, ".volumes.").concat(periodIdx, ".").concat(fieldName),
                })
                : null;
        }).filter(function (error) { return !!error; });
    });
    return errors.length ? new ValidationError(errors) : true;
};
var sumsToTargetByPeriod = function (queueLocations, volumes, volumeEntryMode) {
    if (!(queueLocations === null || queueLocations === void 0 ? void 0 : queueLocations.length))
        return true;
    var totalPeriods = getAllocationPeriodCount(queueLocations, volumes);
    return times(totalPeriods, function (periodIdx) { return periodIdx; }).every(function (periodIdx) {
        var _a, _b;
        var sum = round(sumBy(queueLocations, function (location) { var _a, _b, _c; return Number((_c = (_b = (_a = location.volumes) === null || _a === void 0 ? void 0 : _a[periodIdx]) === null || _b === void 0 ? void 0 : _b.volume) !== null && _c !== void 0 ? _c : 0); }), 4);
        var target = round(Number(volumeEntryMode === 'absolute' ? (_b = (_a = volumes === null || volumes === void 0 ? void 0 : volumes[periodIdx]) === null || _a === void 0 ? void 0 : _a.volume) !== null && _b !== void 0 ? _b : 0 : 100), 4);
        return sum === target;
    });
};
var sumsToTargetByPeriodForProdHours = function (queueLocations, volumes, volumeEntryMode) {
    if (!(queueLocations === null || queueLocations === void 0 ? void 0 : queueLocations.length))
        return true;
    var totalPeriods = getAllocationPeriodCount(queueLocations, volumes);
    return times(totalPeriods, function (periodIdx) { return periodIdx; }).every(function (periodIdx) {
        var _a, _b;
        var sum = round(sumBy(queueLocations, function (location) { var _a, _b, _c; return Number((_c = (_b = (_a = location.volumes) === null || _a === void 0 ? void 0 : _a[periodIdx]) === null || _b === void 0 ? void 0 : _b.productionHour) !== null && _c !== void 0 ? _c : 0); }), 4);
        var target = round(Number(volumeEntryMode === 'absolute' ? (_b = (_a = volumes === null || volumes === void 0 ? void 0 : volumes[periodIdx]) === null || _a === void 0 ? void 0 : _a.productionHour) !== null && _b !== void 0 ? _b : 0 : 100), 4);
        return sum === target;
    });
};
var useWorkVolumeShape = function () {
    var _a, _b;
    var queueData = useQueueAPI()[0];
    return useMemo(function () {
        var _a, _b;
        var isDeferrable = !!((_a = queueData.data) === null || _a === void 0 ? void 0 : _a.isDeferrable);
        var isChatCurrency = [WorkType.Chat, WorkType.SMS, WorkType.Social].includes((_b = queueData.data) === null || _b === void 0 ? void 0 : _b.worktypeId);
        return {
            id: yup.number().required(),
            volumes: generateValidateField('volume'),
            answered: yup
                .number()
                .nullable()
                .min(1)
                .max(99.9)
                .when(['metricTarget', 'threshold'], {
                is: function (metricTarget, threshold) {
                    return !isDeferrable &&
                        (metricTarget === MetricTarget.ServiceLevel || (metricTarget === MetricTarget.ASA && !!threshold));
                },
                then: function (schema) { return schema.required(); },
            }),
            threshold: yup
                .number()
                .nullable()
                .when(['metricTarget', 'answered'], {
                is: function (metricTarget, answered) {
                    return !isDeferrable &&
                        (metricTarget === MetricTarget.ServiceLevel || (metricTarget === MetricTarget.ASA && !!answered));
                },
                then: function (schema) { return schema.required(); },
            }),
            targetAsa: yup
                .number()
                .nullable()
                .when('metricTarget', {
                is: function (metricTarget) { return !isDeferrable && metricTarget === MetricTarget.ASA; },
                then: function (schema) { return schema.required(); },
            }),
            targetOccupancy: yup
                .number()
                .nullable()
                .when('metricTarget', {
                is: function (metricTarget) { return !isDeferrable && metricTarget === MetricTarget.Occupancy; },
                then: function (schema) { return schema.required(); },
            }),
            chatConcurrency: isChatCurrency ? yup.number().required() : yup.number().nullable(),
            factor: isDeferrable ? yup.number().required() : yup.number().nullable(),
            maxOccupancy: yup.number().nullable().min(1),
            patience: yup.number().nullable(),
            retryRate: yup.number().nullable(),
            volumeEntryMode: yup.string().nullable(),
            queueLocations: yup
                .array()
                .of(yup.object().shape({
                volumes: generateValidateField('volume'),
            }))
                .test('location-volume-allocation', 'locationVolumeAllocation', function locationVolumeAllocation(value) {
                var _a, _b, _c;
                var requiredResult = validateRequiredLocationAllocationValues(this, value, (_a = this.parent) === null || _a === void 0 ? void 0 : _a.volumes, 'volume');
                return requiredResult === true
                    ? sumsToTargetByPeriod(value, (_b = this.parent) === null || _b === void 0 ? void 0 : _b.volumes, (_c = this.parent) === null || _c === void 0 ? void 0 : _c.volumeEntryMode)
                    : requiredResult;
            }),
        };
    }, [(_a = queueData.data) === null || _a === void 0 ? void 0 : _a.isDeferrable, (_b = queueData.data) === null || _b === void 0 ? void 0 : _b.worktypeId]);
};
export var shrinkageShape = {
    shrinkages: yup
        .array()
        .of(generateValidateField('percent'))
        .test('sum-of-percent', 'overError', function (shrinkages) {
        if (!shrinkages)
            return true;
        var allRows = shrinkages.flat();
        var byLocation = groupBy(allRows, 'queueLocationId');
        return Object.values(byLocation).every(function (locationRows) {
            var byMonth = groupBy(locationRows, 'month');
            return Object.values(byMonth).every(function (monthRows) {
                var oos = sumBy(monthRows.filter(function (x) { return x.shrinkageCategory === ShrinkageCategory.OutOfOffice; }), 'percent');
                var ios = sumBy(monthRows.filter(function (x) { return x.shrinkageCategory === ShrinkageCategory.InOffice; }), 'percent');
                var total = oos + ((100 - oos) * ios) / 100;
                return round(total, 2) < 100;
            });
        });
    })
        .required(),
};
var hoursPerDaySchema = yup
    .number()
    .nullable()
    .when('totalPartTime', {
    is: function (val) { return !!val && val > 0; },
    then: function (schema) { return schema.required().min(1); },
});
var daysPerWeekSchema = yup
    .number()
    .nullable()
    .when('totalPartTime', {
    is: function (val) { return !!val && val > 0; },
    then: function (schema) { return schema.required().min(1); },
});
export var rolesSchema = yup
    .array()
    .of(yup.object().shape({
    role: yup.string(),
    ratio: yup.number().nullable(),
    salary: yup.number().nullable(),
    staffCalculate: yup.string().nullable(),
    roleAppliesTo: yup.string().nullable(),
    wageType: yup.string().nullable(),
    loadFactor: yup.number().nullable(),
}))
    .test('unique-role', function (roles) {
    var _this = this;
    if (!(roles === null || roles === void 0 ? void 0 : roles.length))
        return true;
    var seen = new Map();
    var duplicated = [];
    for (var idx = 0; idx < roles.length; idx++) {
        var role = roles[idx];
        var key = stripDisplaySuffix(role.role);
        var prevIdx = seen.get(key);
        if (prevIdx === undefined) {
            seen.set(key, idx);
        }
        else {
            if (!duplicated.includes(prevIdx)) {
                duplicated.push(prevIdx);
            }
            if (!duplicated.includes(idx)) {
                duplicated.push(idx);
            }
        }
    }
    if (!duplicated.length) {
        return true;
    }
    return new ValidationError(duplicated.map(function (idx) {
        return _this.createError({
            path: "".concat(_this.path, ".").concat(idx, ".role"),
        });
    }));
})
    .required();
var useWorkingDayShape = function () {
    var _a, _b, _c;
    var queueData = useQueueAPI()[0];
    var status = useQueueStatus();
    return useMemo(function () {
        var _a, _b, _c;
        var isDisableDailyOperating = !!((_a = queueData.data) === null || _a === void 0 ? void 0 : _a.isDeferrable) || !!(status === null || status === void 0 ? void 0 : status.wat);
        var hasQueueLocations = !!((_c = (_b = queueData.data) === null || _b === void 0 ? void 0 : _b.queueLocations) === null || _c === void 0 ? void 0 : _c.length);
        return __assign({ id: yup.number().required(), volumes: generateValidateField('workingDays'), fteDay: hasQueueLocations ? yup.number().nullable() : yup.number().required().min(0.01), timeInterval: yup.number().required(), dailyOperatingHours: yup.lazy(function () {
                return isDisableDailyOperating ? yup.number().nullable() : yup.number().required();
            }) }, (hasQueueLocations
            ? {
                queueLocations: yup
                    .array()
                    .of(yup.object().shape({
                    fteDay: yup.number().required().min(0.01),
                    totalPartTime: yup.number().required(),
                    hoursPerDay: hoursPerDaySchema,
                    daysPerWeek: daysPerWeekSchema,
                }))
                    .required(),
            }
            : {
                totalPartTime: yup.number().required(),
                hoursPerDay: hoursPerDaySchema,
                daysPerWeek: daysPerWeekSchema,
            }));
    }, [(_a = queueData.data) === null || _a === void 0 ? void 0 : _a.isDeferrable, (_c = (_b = queueData.data) === null || _b === void 0 ? void 0 : _b.queueLocations) === null || _c === void 0 ? void 0 : _c.length, status === null || status === void 0 ? void 0 : status.wat]);
};
export var getStaffValidation = function (hasQueueLocations) {
    if (hasQueueLocations === void 0) { hasQueueLocations = false; }
    if (hasQueueLocations) {
        return {
            queueLocations: yup
                .array()
                .of(yup.object().shape({
                roles: rolesSchema,
            }))
                .required(),
        };
    }
    return {
        roles: rolesSchema,
    };
};
var getFinancialValidation = function (hasQueueLocations) {
    if (hasQueueLocations === void 0) { hasQueueLocations = false; }
    if (hasQueueLocations) {
        return {
            queueLocations: yup
                .array()
                .of(yup.object().shape({
                roles: rolesSchema,
                multiplier: yup.number().required(),
                trainWeeks: yup.number().required(),
                volumes: generateValidateField('rate'),
            }))
                .required(),
        };
    }
    return __assign(__assign(__assign({}, getStaffValidation(hasQueueLocations)), getVolumesValidation(['rate'], hasQueueLocations)), { multiplier: yup.number().required(), trainWeeks: yup.number().required() });
};
var useWAPVolumeShape = function () {
    var months = useWAVMonths();
    return useMemo(function () {
        var shape = {};
        for (var _i = 0, months_1 = months; _i < months_1.length; _i++) {
            var month = months_1[_i];
            shape["month".concat(month)] = yup
                .array()
                .of(yup
                .array()
                .of(yup.object().shape({
                workVolume: yup.number().min(0).required(),
            }))
                .required())
                .required()
                .test('sum-of-percent', 'percentage', function (volumes) {
                return volumes.flat().some(function (_a) {
                    var workVolume = _a.workVolume;
                    return workVolume === null;
                }) ||
                    volumes.every(function (arr) { return chain(arr).sumBy('workVolume').round(4).value() === 100; });
            });
        }
        return {
            id: yup.number().required(),
            wapVolumes: yup.object().shape(shape),
        };
    }, [months]);
};
export var useValidateForm = function () {
    var _a, _b, _c, _d;
    var workingDayShape = useWorkingDayShape();
    var wapVolumeShape = useWAPVolumeShape();
    var workVolumeShape = useWorkVolumeShape();
    var step = useWizardParams().step;
    var planTypeCategory = usePlanTypeCategory();
    var queueData = useQueueAPI()[0];
    var status = useQueueStatus();
    var hasQueueLocations = !!((_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.queueLocations) === null || _b === void 0 ? void 0 : _b.length);
    var schemaByStep = useMemo(function () {
        var _a;
        var _b;
        var shapeMap = (_a = {},
            _a[WizardStep.WorkVolume] = yup.object().shape(__assign(__assign({}, workVolumeShape), wapVolumeShape), [
                ['threshold', 'answered'],
                ['patience', 'retryRate'],
            ]),
            _a[WizardStep.ProdHours] = yup.object().shape(__assign(__assign({ volumes: generateValidateField('productionHour', 'prodOccupancy') }, wapVolumeShape), { targetOccupancy: yup
                    .number()
                    .nullable()
                    .min(0.01, 'Target occupancy must be greater than 0')
                    .max(100, 'Target occupancy cannot exceed 100'), factor: yup
                    .number()
                    .nullable()
                    .min(1, 'Productivity factor must be at least 1')
                    .max(100, 'Productivity factor cannot exceed 100'), volumeEntryMode: yup.string().nullable(), queueLocations: yup
                    .array()
                    .of(yup.object().shape({
                    volumes: generateValidateField('productionHour'),
                }))
                    .test('location-prod-hours-allocation', 'locationVolumeAllocation', function locationProdHoursAllocation(value) {
                    var _a, _b, _c;
                    if (!hasQueueLocations)
                        return true;
                    var requiredResult = validateRequiredLocationAllocationValues(this, value, (_a = this.parent) === null || _a === void 0 ? void 0 : _a.volumes, 'productionHour');
                    return requiredResult === true
                        ? sumsToTargetByPeriodForProdHours(value, (_b = this.parent) === null || _b === void 0 ? void 0 : _b.volumes, (_c = this.parent) === null || _c === void 0 ? void 0 : _c.volumeEntryMode)
                        : requiredResult;
                }) })),
            _a[WizardStep.HandleTime] = yup
                .object()
                .shape(getVolumesValidation(['handleTime', 'learnCurve'], hasQueueLocations)),
            _a[WizardStep.Shrinkage] = yup.object().shape(shrinkageShape),
            _a[WizardStep.Attrition] = yup.object().shape(getVolumesValidation(['attrition'], hasQueueLocations)),
            _a[WizardStep.WorkingDays] = yup.object().shape(workingDayShape),
            _a[WizardStep.SupportStaff] = yup.object().shape(getStaffValidation(hasQueueLocations)),
            _a[WizardStep.Financial] = yup.object().shape(getFinancialValidation(hasQueueLocations)),
            _a[WizardStep.Review] = (function () {
                var _a, _b;
                if (hasQueueLocations) {
                    var baseSchema = __assign(__assign(__assign({}, shrinkageShape), wapVolumeShape), { id: yup.number().required(), multiplier: yup.number().nullable(), trainWeeks: yup.number().nullable(), fteDay: yup.number().nullable(), timeInterval: yup.number().required(), dailyOperatingHours: yup.lazy(function () {
                            var _a;
                            var isDisableDailyOperating = !!((_a = queueData.data) === null || _a === void 0 ? void 0 : _a.isDeferrable) || !!(status === null || status === void 0 ? void 0 : status.wat);
                            return isDisableDailyOperating ? yup.number().nullable() : yup.number().required();
                        }) });
                    if (planTypeCategory === PlanTypeCategory.ProdHours) {
                        return yup.object().shape(__assign(__assign({}, baseSchema), { volumes: generateValidateField('workingDays', 'productionHour', 'prodOccupancy'), targetOccupancy: yup
                                .number()
                                .nullable()
                                .min(0.01, 'Target occupancy must be greater than 0')
                                .max(100, 'Target occupancy cannot exceed 100'), factor: yup
                                .number()
                                .nullable()
                                .min(1, 'Productivity factor must be at least 1')
                                .max(100, 'Productivity factor cannot exceed 100'), volumeEntryMode: yup.string().nullable(), queueLocations: yup
                                .array()
                                .of(yup.object().shape({
                                fteDay: yup.number().nullable().min(0.01),
                                totalPartTime: yup.number().nullable(),
                                hoursPerDay: hoursPerDaySchema,
                                daysPerWeek: daysPerWeekSchema,
                                roles: rolesSchema,
                                multiplier: yup.number().required(),
                                trainWeeks: yup.number().required(),
                                volumes: generateValidateField('attrition', 'rate', 'productionHour', 'prodOccupancy'),
                            }))
                                .test('location-prod-hours-allocation', 'locationVolumeAllocation', function locationProdHoursAllocation(value) {
                                var _a, _b, _c;
                                var requiredResult = validateRequiredLocationAllocationValues(this, value, (_a = this.parent) === null || _a === void 0 ? void 0 : _a.volumes, 'productionHour');
                                return requiredResult === true
                                    ? sumsToTargetByPeriodForProdHours(value, (_b = this.parent) === null || _b === void 0 ? void 0 : _b.volumes, (_c = this.parent) === null || _c === void 0 ? void 0 : _c.volumeEntryMode)
                                    : requiredResult;
                            })
                                .required() }));
                    }
                    else {
                        var isDeferrable_1 = !!((_a = queueData.data) === null || _a === void 0 ? void 0 : _a.isDeferrable);
                        var isChatCurrency = [WorkType.Chat, WorkType.SMS, WorkType.Social].includes((_b = queueData.data) === null || _b === void 0 ? void 0 : _b.worktypeId);
                        return yup.object().shape(__assign(__assign({}, baseSchema), { volumes: generateValidateField('volume', 'workingDays'), answered: yup
                                .number()
                                .nullable()
                                .min(1)
                                .max(99.9)
                                .when(['metricTarget', 'threshold'], {
                                is: function (metricTarget, threshold) {
                                    return !isDeferrable_1 &&
                                        (metricTarget === MetricTarget.ServiceLevel ||
                                            (metricTarget === MetricTarget.ASA && !!threshold));
                                },
                                then: function (schema) { return schema.required(); },
                            }), threshold: yup
                                .number()
                                .nullable()
                                .when(['metricTarget', 'answered'], {
                                is: function (metricTarget, answered) {
                                    return !isDeferrable_1 &&
                                        (metricTarget === MetricTarget.ServiceLevel || (metricTarget === MetricTarget.ASA && !!answered));
                                },
                                then: function (schema) { return schema.required(); },
                            }), targetAsa: yup
                                .number()
                                .nullable()
                                .when('metricTarget', {
                                is: function (metricTarget) { return !isDeferrable_1 && metricTarget === MetricTarget.ASA; },
                                then: function (schema) { return schema.required(); },
                            }), targetOccupancy: yup
                                .number()
                                .nullable()
                                .when('metricTarget', {
                                is: function (metricTarget) { return !isDeferrable_1 && metricTarget === MetricTarget.Occupancy; },
                                then: function (schema) { return schema.required(); },
                            }), chatConcurrency: isChatCurrency ? yup.number().required() : yup.number().nullable(), factor: isDeferrable_1 ? yup.number().required() : yup.number().nullable(), maxOccupancy: yup.number().nullable(), patience: yup.number().nullable(), retryRate: yup.number().nullable(), volumeEntryMode: yup.string().nullable(), queueLocations: yup
                                .array()
                                .of(yup.object().shape({
                                fteDay: yup.number().nullable().min(0.01),
                                totalPartTime: yup.number().nullable(),
                                hoursPerDay: hoursPerDaySchema,
                                daysPerWeek: daysPerWeekSchema,
                                roles: rolesSchema,
                                multiplier: yup.number().required(),
                                trainWeeks: yup.number().required(),
                                volumes: generateValidateField('volume', 'handleTime', 'learnCurve', 'attrition', 'rate'),
                            }))
                                .test('location-volume-allocation', 'locationVolumeAllocation', function locationVolumeAllocation(value) {
                                var _a, _b, _c;
                                var requiredResult = validateRequiredLocationAllocationValues(this, value, (_a = this.parent) === null || _a === void 0 ? void 0 : _a.volumes, 'volume');
                                return requiredResult === true
                                    ? sumsToTargetByPeriod(value, (_b = this.parent) === null || _b === void 0 ? void 0 : _b.volumes, (_c = this.parent) === null || _c === void 0 ? void 0 : _c.volumeEntryMode)
                                    : requiredResult;
                            })
                                .required() }), [
                            ['threshold', 'answered'],
                            ['patience', 'retryRate'],
                        ]);
                    }
                }
                else if (planTypeCategory === PlanTypeCategory.ProdHours) {
                    return yup.object().shape(__assign(__assign(__assign(__assign(__assign({}, shrinkageShape), workingDayShape), getFinancialValidation(hasQueueLocations)), wapVolumeShape), { id: yup.number().required(), volumes: generateValidateField('workingDays', 'attrition', 'rate', 'productionHour', 'prodOccupancy'), targetOccupancy: yup
                            .number()
                            .nullable()
                            .min(0.01, 'Target occupancy must be greater than 0')
                            .max(100, 'Target occupancy cannot exceed 100'), factor: yup
                            .number()
                            .nullable()
                            .min(1, 'Productivity factor must be at least 1')
                            .max(100, 'Productivity factor cannot exceed 100') }));
                }
                else {
                    return yup.object().shape(__assign(__assign(__assign(__assign(__assign(__assign({}, shrinkageShape), workVolumeShape), workingDayShape), getFinancialValidation(hasQueueLocations)), wapVolumeShape), { volumes: generateValidateField('volume', 'handleTime', 'learnCurve', 'workingDays', 'attrition', 'rate') }), [
                        ['threshold', 'answered'],
                        ['patience', 'retryRate'],
                    ]);
                }
            })(),
            _a);
        return ((_b = shapeMap[step]) !== null && _b !== void 0 ? _b : yup.object().shape({}));
    }, [
        workVolumeShape,
        wapVolumeShape,
        hasQueueLocations,
        workingDayShape,
        planTypeCategory,
        step,
        (_c = queueData.data) === null || _c === void 0 ? void 0 : _c.isDeferrable,
        (_d = queueData.data) === null || _d === void 0 ? void 0 : _d.worktypeId,
        status === null || status === void 0 ? void 0 : status.wat,
    ]);
    return schemaByStep;
};
